import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { sourceLabel } from '../../lib/leads/sourceLabels';
import { ConversationSummaryCard } from './ConversationSummaryCard';
import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import { CallLog, type CallLogProps } from './CallLog';
import { historySpeaker, type LeadHistoryEntry, type LeadHistoryPage } from '../../lib/speedToLead/leadHistory';

// THE LEAD HISTORY PANEL (Speed to Lead Stage 4C), shared by Kyck and the club's portal. One lead (club + phone), newest
// first: enquiries, every text conversation with its messages (system events as notes), every call with its outcome,
// summary, labelled transcript and playback while the audio exists, do-not-contact / opt-out changes, and (Phase 2) a
// person's outcome and "needs a person" changes.
//
// ⚠️ It LOADS WHEN IT OPENS (mounting this component is the only trigger) and then only on "Show older": it is never
// mounted per row, so a page of calls costs nothing until somebody asks. It is a read: no model call, ever.
// The host passes `load` (the handle is opaque: the phone number never appears in a URL or on this component).
//
// ⚠️ PHASE 2: `initialPage` lets the lead split view hand over the FIRST page it already has (the detail response carries it),
// so opening a lead is ONE request, and a 30-second refresh of that page replaces the first page in place without losing
// the older pages already loaded or flickering the list.

export interface LeadHistoryPanelProps {
  load: (cursor: string | null) => Promise<LeadHistoryPage>;
  /** The same call handlers the call log uses (playback, outcome confirmation) so a call here behaves identically. */
  callHandlers: Pick<CallLogProps, 'loadPlayUrl' | 'onConfirmOutcome'>;
  /** Confirm the outcome suggested by a text conversation's AI summary. Absent: the suggestion is shown as text only. */
  confirmConversationOutcome?: (conversationId: string, code: LeadOutcome) => Promise<void>;
  /** The first page, already loaded by the host. Present = no request is made on mount. */
  initialPage?: LeadHistoryPage;
}

const when = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
};

const EVENT_TEXT: Record<'do_not_contact' | 'opted_out' | 'opted_in', string> = {
  do_not_contact: 'Marked do not contact',
  opted_out: 'Opted out of texts',
  opted_in: 'Opted back in to texts',
};

/** "Outcome set to Tour booked by Rachel" / "Needs a person turned on by Rachel". The outcome label comes from the ONE outcome table. */
export function leadEventText(e: Extract<LeadHistoryEntry, { kind: 'lead_event' }>): string {
  if (e.event === 'outcome_set') return `Outcome set to ${e.outcome ? LEAD_OUTCOME_LABELS[e.outcome] : 'a new value'} by ${e.by}`;
  return `Needs a person turned ${e.event === 'needs_person_on' ? 'on' : 'off'} by ${e.by}`;
}

function Entry({ e, callHandlers, assistantName, confirmConversationOutcome }: { e: LeadHistoryEntry; assistantName: string | null } & Pick<LeadHistoryPanelProps, 'callHandlers' | 'confirmConversationOutcome'>) {
  if (e.kind === 'intake') {
    return (
      <li className="kc-card rounded border border-border p-2 text-sm" data-testid="history-intake">
        <span className="font-medium text-foreground">Enquiry received</span>
        {e.source && <span className="text-muted-foreground"> · {sourceLabel(e.source)}</span>}
        <span className="text-muted-foreground"> · {when(e.at)}</span>
      </li>
    );
  }
  if (e.kind === 'event') {
    return (
      <li className="rounded border border-dashed border-border p-2 text-sm text-muted-foreground" data-testid="history-event">
        {when(e.at)} · {EVENT_TEXT[e.event]}{e.note ? ` (${e.note})` : ''}
      </li>
    );
  }
  if (e.kind === 'lead_event') {
    return (
      <li className="rounded border border-dashed border-border p-2 text-sm text-muted-foreground" data-testid="history-lead-event">
        {when(e.at)} · {leadEventText(e)}
      </li>
    );
  }
  if (e.kind === 'call') {
    // The same row the call log shows, so playback, the labelled transcript, the summary and outcome work identically.
    return (
      <li data-testid="history-call">
        <CallLog calls={[e.row]} {...callHandlers} />
      </li>
    );
  }
  return (
    <li className="kc-card space-y-2 rounded border border-border p-2 text-sm" data-testid="history-conversation">
      <p>
        <span className="text-muted-foreground">{when(e.at)}</span> <span className="font-medium text-foreground">Text conversation</span>
        <span className="text-muted-foreground"> · {e.state} · {e.outcomeLabel}</span>
      </p>
      <ConversationSummaryCard
        id={`history-outcome-${e.id}`}
        summary={e.summary ?? null}
        current={e.outcome}
        onSave={confirmConversationOutcome ? (code) => confirmConversationOutcome(e.id, code) : undefined}
      />
      <ul className="space-y-1.5">
        {e.messages.map((m) => (
          <li
            key={m.id}
            className={m.from === 'note' ? 'px-1 text-xs italic text-muted-foreground' : `flex ${m.from === 'lead' ? 'justify-start' : 'justify-end'}`}
            data-testid={m.from === 'note' ? 'history-note' : 'history-message'}
            data-from={m.from}
          >
            {m.from === 'note' ? (
              <span>{m.text}</span>
            ) : (
              <span
                className={`kc-bubble max-w-[85%] rounded-lg px-3 py-1.5 ${m.from === 'lead' ? 'bg-muted text-foreground' : 'bg-primary/15 text-foreground'}`}
                data-bubble={m.from}
              >
                <span className="block text-xs text-muted-foreground">
                  {historySpeaker(m.from, assistantName)}
                  {m.byStaff ? ' · sent by staff' : ''}
                </span>
                {m.text}
              </span>
            )}
          </li>
        ))}
      </ul>
      {e.truncated && <p className="text-xs text-muted-foreground">Showing the latest {e.messages.length} of {e.messageCount} messages.</p>}
    </li>
  );
}

export function LeadHistoryPanel({ load, callHandlers, confirmConversationOutcome, initialPage }: LeadHistoryPanelProps) {
  // With an `initialPage` the first page is the host's (and follows its refreshes); everything older is this panel's own.
  const [loaded, setLoaded] = useState<LeadHistoryEntry[]>([]);
  const [next, setNext] = useState<string | null>(initialPage?.nextCursor ?? null);
  const [total, setTotal] = useState<number | null>(initialPage?.total ?? null);
  const [assistantName, setAssistantName] = useState<string | null>(initialPage?.assistantName ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = async (cursor: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const page = await load(cursor);
      setLoaded((cur) => (cursor ? [...cur, ...page.entries] : page.entries));
      setNext(page.nextCursor);
      setTotal(page.total);
      setAssistantName(page.assistantName ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the history.');
    } finally {
      setBusy(false);
    }
  };

  // The panel mounts only when somebody opens it: this effect IS the "load on open". A host that already has the first page skips it.
  useEffect(() => {
    if (!initialPage) void fetchPage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The host's first page changed (its refresh): the total and the name follow it; the older pages already loaded stay.
  const firstKey = initialPage ? `${initialPage.total}|${initialPage.entries[0]?.id ?? ''}` : '';
  useEffect(() => {
    if (!initialPage) return;
    setTotal(initialPage.total);
    setAssistantName(initialPage.assistantName ?? null);
    if (loaded.length === 0) setNext(initialPage.nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstKey]);

  const seen = new Set<string>();
  const entries = [...(initialPage?.entries ?? []), ...loaded].filter((e) => {
    const k = `${e.kind}-${e.id}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return (
    <div className="space-y-2 rounded border border-border bg-muted/30 p-3" data-testid="lead-history">
      <p className="font-medium text-foreground">Lead history{total !== null ? ` (${total})` : ''}</p>
      {busy && entries.length === 0 && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {!busy && !error && entries.length === 0 && <p className="text-sm text-muted-foreground">Nothing recorded for this lead yet.</p>}
      <ol className="space-y-2" aria-label="Lead history, newest first">
        {entries.map((e) => <Entry key={`${e.kind}-${e.id}`} e={e} callHandlers={callHandlers} assistantName={assistantName} confirmConversationOutcome={confirmConversationOutcome} />)}
      </ol>
      {next && <Button type="button" variant="outline" size="sm" onClick={() => void fetchPage(loaded.length === 0 && initialPage ? initialPage.nextCursor : next)} disabled={busy}>{busy ? 'Loading…' : 'Show older'}</Button>}
    </div>
  );
}
